<template>
  <div class="modal-loyalty px-4 py-6">
    <div class="modal-loyalty__grid">
      <div class="text-3xl font-semibold mb-4">Привилегии</div>

      <div class="flex items-center justify-between border-b pb-4 mb-4">
        <div class="flex flex-col items-center flex-1">
          <span class="text-gray-500 text-xs">Оплата</span>
          <span class="font-bold text-xl">
            {{ maxRedeemPercent }}%
          </span>
        </div>
        <div class="flex flex-col items-center flex-1">
          <span class="text-gray-500 text-xs">Кэшбэк</span>
          <span class="font-bold text-xl">{{ currentCashbackPercent }}%</span>
        </div>
        <div class="flex flex-col items-center flex-1">
          <span class="bg-yellow-600 flex items-center px-5 py-2 rounded-full text-lg font-semibold gap-2">
            Баллы
            <UIcon name="i-ep-coin" class="w-4 h-4" />
          </span>
        </div>
      </div>

      <div>
        <h3 class="text-2xl font-semibold mb-2">Как всё работает</h3>

        <!--
          Бонус за регистрацию. Режим приветствия задаётся в настройках лояльности и
          взаимоисключающий: ЛИБО скидка на первые N заказов (лесенка из БД), ЛИБО
          начисление N приветственных баллов. Показываем ровно один блок.
        -->
        <div v-if="showFirstOrdersDiscount" class="mb-6">
          <div class="font-bold text-lg mb-1">Скидка на первые заказы</div>
          <div class="text-gray-700 mb-2 dark:text-gray-300">
            За регистрацию дарим повышенную скидку на первые
            {{ firstOrderSteps.length }} {{ ordersWord }}:
          </div>
          <ul class="grid gap-1 text-gray-700 dark:text-gray-300">
            <li v-for="step in firstOrderSteps" :key="step.step_number" class="flex items-center gap-2">
              <UIcon name="i-heroicons-gift" class="w-4 h-4 flex-shrink-0 text-yellow-600" />
              <span>{{ step.step_number }}-й заказ — <b>{{ formatPercent(step.discount_percent) }}%</b></span>
            </li>
          </ul>
          <div
            v-if="firstOrdersMinAmount > 0 || firstOrdersMaxDiscount > 0"
            class="mt-2 text-sm text-gray-500 dark:text-gray-400"
          >
            <template v-if="firstOrdersMinAmount > 0">
              Действует на заказы от {{ firstOrdersMinAmount.toLocaleString('ru-RU') }} ₽.
            </template>
            <template v-if="firstOrdersMaxDiscount > 0">
              Максимальная скидка — {{ firstOrdersMaxDiscount.toLocaleString('ru-RU') }} ₽ на заказ.
            </template>
          </div>
        </div>

        <div v-else-if="showWelcomePoints" class="mb-6">
          <div class="font-bold text-lg mb-1">Приветственные баллы</div>
          <div class="text-gray-700 mb-2 dark:text-gray-300">
            За регистрацию начисляем <b>{{ welcomeBonusPoints.toLocaleString('ru-RU') }}</b>
            приветственных {{ pointsWord }} — это ваша скидка на первый заказ.
          </div>
        </div>

        <div class="mb-6">
          <div class="font-bold text-lg mb-1">Что такое кэшбэк?</div>
          <div class="text-gray-700 mb-2 dark:text-gray-300">
            Возврат части стоимости заказов, который вы получаете в баллах. Процент кэшбэка зависит от общей суммы
            заказов в текущем периоде.
            <template v-for="(level, index) in (storeInstitution.institution as any)?.loyalty_levels" :key="index">
              <span>{{ (level.percent * 100).toFixed(1) }}% — от {{ level.min_spent.toLocaleString('ru-RU') }} ₽</span>
              <span v-if="index !== (storeInstitution.institution as any)?.loyalty_levels?.length - 1">,</span>
            </template>
          </div>
        </div>

        <div class="mb-6">
          <div class="font-bold text-lg mb-1">Как повышать кэшбэк?</div>
          <div class="text-gray-700 mb-2 dark:text-gray-300">
            Кэшбэк определяется по сумме заказов за текущий период: чем больше сумма, тем выше ваш процент будет в
            следующем периоде.
          </div>
        </div>

        <div>
          <div class="font-bold text-lg mb-1">Что такое баллы?</div>
          <div class="text-gray-700 dark:text-gray-300">
            1 балл = 1 ₽. Вы можете оплатить баллами до {{ maxRedeemPercent }}% от суммы заказа. Баллы нельзя
            использовать вместе со скидками и акциями.
            <template v-if="redeemRules.length">
              {{ redeemRules.join(' ') }}
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted } from 'vue';
import { useInstitutionStore } from '@entities/institution';
import { useLoyaltyStore } from '@entities/loyalty';

const storeInstitution = useInstitutionStore();
const loyaltyStore = useLoyaltyStore();

const institution = computed(() => storeInstitution.institution as any);

// Режим приветствия новичка: приветственные баллы ИЛИ скидка на первые заказы (из настроек лояльности)
const welcomeMode = computed<'bonus_points' | 'first_orders_discount'>(
  () => institution.value?.loyalty_welcome_mode ?? 'bonus_points',
);
const welcomeBonusPoints = computed(() => Number(institution.value?.loyalty_welcome_bonus_points) || 0);

/** Реальный лимит заведения, а не «до 50%» текстом: у заведений он разный. */
const maxRedeemPercent = computed(() => Math.round(Number(institution.value?.loyalty_max_percent_redeem ?? 0.5) * 100));

/**
 * Условия списания баллов из настроек заведения. Без них экран обещал списание,
 * которого на первом заказе или мелком чеке не будет — гость узнавал бы об этом
 * только в корзине.
 */
const redeemRules = computed<string[]>(() => {
  const rules: string[] = [];

  const minOrderAmount = Number(institution.value?.loyalty_redeem_min_order_amount) || 0;
  if (minOrderAmount > 0) {
    rules.push(`Списание доступно на заказах от ${minOrderAmount.toLocaleString('ru-RU')} ₽.`);
  }

  const fromOrderNumber = Number(institution.value?.loyalty_redeem_from_order_number) || 1;
  if (fromOrderNumber > 1) {
    rules.push(
      fromOrderNumber === 2
        ? 'Списывать баллы можно начиная со второго заказа.'
        : `Списывать баллы можно начиная с ${fromOrderNumber}-го заказа.`,
    );
  }

  return rules;
});

// Шаги «лесенки» скидок на первые заказы ({ step_number, discount_percent: доля 0..1 }) из БД
const firstOrderSteps = computed<Array<{ step_number: number; discount_percent: number }>>(() => {
  const steps = institution.value?.loyalty_first_order_steps;
  if (!Array.isArray(steps)) return [];
  return steps
    .map((s: any) => ({ step_number: Number(s.step_number), discount_percent: Number(s.discount_percent) }))
    .filter((s) => s.step_number > 0 && s.discount_percent > 0)
    .sort((a, b) => a.step_number - b.step_number);
});

const firstOrdersMinAmount = computed(() => Number(institution.value?.loyalty_first_orders_min_order_amount) || 0);
const firstOrdersMaxDiscount = computed(() => Number(institution.value?.loyalty_first_orders_max_discount) || 0);

// Показываем ровно один блок «бонус за регистрацию» в зависимости от режима
const showFirstOrdersDiscount = computed(
  () => welcomeMode.value === 'first_orders_discount' && firstOrderSteps.value.length > 0,
);
const showWelcomePoints = computed(() => welcomeMode.value === 'bonus_points' && welcomeBonusPoints.value > 0);

/** Склонение русского слова по числу: [1, 2-4, 5+]. */
function pluralize(n: number, forms: [string, string, string]): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return forms[0];
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return forms[1];
  return forms[2];
}
const ordersWord = computed(() => pluralize(firstOrderSteps.value.length, ['заказ', 'заказа', 'заказов']));
const pointsWord = computed(() => pluralize(welcomeBonusPoints.value, ['балл', 'балла', 'баллов']));

/** Доля 0..1 → строка процента без лишних нулей (0.15 → «15», 0.125 → «12.5»). */
function formatPercent(fraction: number): string {
  const pct = fraction * 100;
  return Number.isInteger(pct) ? String(pct) : pct.toFixed(1);
}

/**
 * Получаем текущий процент кэшбэка пользователя
 */
const currentCashbackPercent = computed(() => {
  // Получаем cashbackLevel из данных пользователя
  const cashbackLevel = loyaltyStore.loyaltyInfo?.cashbackLevel;

  if (!cashbackLevel) {
    // Если нет данных, показываем базовый уровень
    const levels = (storeInstitution.institution as any)?.loyalty_levels || [];
    if (!levels.length) return 0;
    return Math.round(levels[0].percent * 100);
  }

  // Парсим процент из строки типа "3%" или "5%"
  const percentValue = typeof cashbackLevel === 'string' ? Number.parseFloat(cashbackLevel.replace('%', '')) : cashbackLevel;

  return Math.round(percentValue);
});

/**
 * Загружаем информацию о лояльности при открытии модального окна
 */
onMounted(() => {
  loyaltyStore.fetchLoyaltyInfo();
});
</script>

<style scoped lang="scss">
.modal-loyalty {
  @apply p-2.5 bg-white dark:bg-gray-900;

  &__grid {
    @apply bg-sky-300 dark:bg-zinc-800 rounded-2xl pt-20 pb-4 px-5 md:py-8 md:px-8
    grid gap-5 max-md:h-[calc(100vh-20px)]
    content-baseline overflow-y-auto overscroll-contain;
  }
}
</style>
